import { useEffect, useMemo, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { Search, Download as DownloadIcon, Star, FileText, FolderOpen } from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingBlock } from "@/components/ui/loading-block";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Seo } from "@/components/seo/Seo";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedField } from "@/hooks/useLocalizedField";
import { Price } from "@/components/pricing/Price";
import { DownloadCover } from "@/components/download/DownloadCover";

interface DownloadRow {
  id: string;
  slug: string;
  title_en: string | null;
  title_es: string | null;
  title_fr: string | null;
  title_pt: string | null;
  description_en: string | null;
  description_es: string | null;
  description_fr: string | null;
  description_pt: string | null;
  cover_image_url: string | null;
  category: string | null;
  file_kind: string | null;
  price_eur: number;
  total_pages: number | null;
  author_id: string | null;
}

const KINDS = ["all", "playbook", "template", "deck", "framework", "report", "pdf", "zip"] as const;

export default function DownloadsIndex() {
  const { t } = useTranslation();
  const pick = useLocalizedField();
  const [rows, setRows] = useState<DownloadRow[] | null>(null);
  const [kind, setKind] = useState<string>("all");
  const [price, setPrice] = useState<string>("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("downloads")
        .select("id, slug, title_en, title_es, title_fr, title_pt, description_en, description_es, description_fr, description_pt, cover_image_url, category, file_kind, price_eur, total_pages, author_id")
        .eq("published", true)
        .order("created_at", { ascending: false });
      setRows(data ?? []);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => {
      if (kind !== "all" && r.file_kind !== kind) return false;
      if (price === "free" && Number(r.price_eur) !== 0) return false;
      if (price === "paid" && Number(r.price_eur) === 0) return false;
      if (q) {
        const title = (pick(r, "title") || "").toLowerCase();
        if (!title.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, kind, price, q, pick]);

  return (
    <PublicShell>
      <Seo title={t("downloads.seoTitle", "Downloads — Bootpreneurs")} description={t("downloads.seoDesc", "Operational resources, playbooks, templates and frameworks for emerging-market founders.")} />
      <section className="border-b border-border bg-gradient-to-br from-primary/5 to-background">
        <div className="container py-12 md:py-16">
          <Badge variant="outline" className="mb-3">{t("downloads.library.tag")}</Badge>
          <h1 className="font-display text-3xl font-bold md:text-4xl">{t("downloads.library.title")}</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t("downloads.library.sub")}</p>
        </div>
      </section>

      <section className="container py-8">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList className="flex-wrap">
              {KINDS.map((k) => (
                <TabsTrigger key={k} value={k}>{t(`downloads.tabs.${k}`, k)}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("downloads.search.placeholder")} className="pl-8 w-56" />
            </div>
            <Select value={price} onValueChange={setPrice}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("downloads.priceFilter.all")}</SelectItem>
                <SelectItem value="free">{t("downloads.priceFilter.free")}</SelectItem>
                <SelectItem value="paid">{t("downloads.priceFilter.paid")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!rows ? (
          <LoadingBlock variant="card-grid" count={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            illustration={<FolderOpen className="h-10 w-10" />}
            title={t("downloads.empty", { defaultValue: "No resources match your filters." })}
          />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((d) => {
              const title = pick(d, "title");
              const desc = pick(d, "description");
              const isFree = Number(d.price_eur) === 0;
              return (
                <Link key={d.id} to={`/downloads/${d.slug}`} className="group">
                  <Card className="h-full overflow-hidden transition-all hover:border-primary/40 hover:shadow-md">
                    <div className="relative aspect-[16/10] bg-gradient-to-br from-primary/20 to-accent/20">
                      {d.cover_image_url ? (
                        <img src={d.cover_image_url} alt={title} loading="lazy" className="h-full w-full object-cover" />
                      ) : (
                        <div className="absolute inset-0">
                          <DownloadCover slug={d.slug} title={title} description={desc ?? undefined} size="sm" className="h-full w-full" />
                        </div>
                      )}
                      {isFree && <Badge className="absolute left-3 top-3">{t("downloads.badge.free")}</Badge>}
                      {d.file_kind && <Badge variant="secondary" className="absolute right-3 top-3 capitalize">{d.file_kind}</Badge>}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="line-clamp-2 font-semibold group-hover:text-primary">{title}</h3>
                      {desc && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{desc}</p>}
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3 text-muted-foreground">
                          {d.total_pages && <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{d.total_pages}p</span>}
                          <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5" />4.8</span>
                        </div>
                        {isFree ? (
                          <span className="font-semibold">{t("downloads.badge.free")}</span>
                        ) : (
                          <Price size="inline" eurCents={Math.round(Number(d.price_eur) * 100)} />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </PublicShell>
  );
}
